import { useMemo, useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import { toCsv, copyToClipboard } from '@/lib/csv';

export interface ReportColumn<T> {
  key: string;
  label: string;
  /** Render the cell value. Defaults to String((row as Record<string, unknown>)[key]). */
  render?: (row: T) => ReactNode;
  align?: 'left' | 'right';
  /** Allow the cell to wrap instead of staying on a single line (e.g. URLs). */
  wrap?: boolean;
  /**
   * Raw value used for CSV export. Defaults to row[key]. Define this for
   * computed columns whose displayed value differs from a single field.
   */
  csvValue?: (row: T) => unknown;
}

interface ReportTableProps<T> {
  rows: T[];
  columns: ReportColumn<T>[];
  /** Cap the number of rows initially shown; the rest are revealed on demand. */
  initialLimit?: number;
  emptyLabel?: string;
}

/**
 * Lightweight read-only table for report sections, with an optional
 * "show more" cap so large datasets (url_metrics, executions_data) stay snappy.
 */
export function ReportTable<T>({
  rows,
  columns,
  initialLimit,
  emptyLabel = 'No data',
}: ReportTableProps<T>) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const visibleRows = useMemo(() => {
    if (expanded || !initialLimit) return rows;
    return rows.slice(0, initialLimit);
  }, [rows, expanded, initialLimit]);

  // Always export the full dataset, not just the visible slice.
  const handleCopyCsv = async () => {
    const headers = columns.map((c) => c.label);
    const matrix = rows.map((row) =>
      columns.map((col) =>
        col.csvValue ? col.csvValue(row) : (row as Record<string, unknown>)[col.key]
      )
    );
    try {
      await copyToClipboard(toCsv(headers, matrix));
      setCopied(true);
      toast.success(`Copied ${rows.length} rows as CSV`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Failed to copy CSV');
    }
  };

  if (rows.length === 0) {
    return <p className="text-adv-body-s text-adv-tertiary py-4">{emptyLabel}</p>;
  }

  const hidden = rows.length - visibleRows.length;

  return (
    <div>
      <div className="flex justify-end mb-2">
        <button
          onClick={handleCopyCsv}
          className="adv-btn adv-btn--secondary adv-btn--sm"
          title="Copy all rows as CSV"
        >
          {copied ? <Check size={14} className="text-adv-success" /> : <Copy size={14} />}
          Copy as CSV
        </button>
      </div>
      <div className="overflow-x-auto border border-adv-border rounded-adv-lg bg-adv-card">
        <table className="adv-table adv-table--compact min-w-full">
          <thead className="bg-adv-sunken">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-3 py-2 ${
                    col.align === 'right' ? 'text-right' : 'text-left'
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, idx) => (
              <tr key={idx} className="hover:bg-adv-sunken">
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-3 py-2 ${
                      col.wrap ? 'align-top' : 'whitespace-nowrap'
                    } ${col.align === 'right' ? 'text-right adv-num' : 'text-left'}`}
                  >
                    {col.render
                      ? col.render(row)
                      : String((row as Record<string, unknown>)[col.key] ?? '-')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hidden > 0 && (
        <button
          onClick={() => setExpanded(true)}
          className="adv-link mt-2 text-adv-body-s font-bold"
        >
          Show {hidden} more rows
        </button>
      )}
      {expanded && initialLimit && rows.length > initialLimit && (
        <button
          onClick={() => setExpanded(false)}
          className="adv-link mt-2 ml-3 text-adv-body-s"
        >
          Show less
        </button>
      )}
    </div>
  );
}
